'use client';

import { useEffect, useRef, useState } from 'react';
import Lottie from 'lottie-react';

interface LazyLottieProps {
  src: string;
  loop?: boolean;
  autoplay?: boolean;
  className?: string;
  style?: React.CSSProperties;
  /** Load immediately without waiting for viewport (use for above-fold animations) */
  eager?: boolean;
}

const cache = new Map<string, object>();

export function LazyLottie({
  src,
  loop = true,
  autoplay = true,
  className,
  style,
  eager = false,
}: LazyLottieProps) {
  const [isVisible, setIsVisible] = useState(eager);
  const [animationData, setAnimationData] = useState<object | null>(
    () => cache.get(src) ?? null
  );
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (eager) return;
    const el = containerRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [eager]);

  useEffect(() => {
    if (!isVisible) return;
    if (cache.has(src)) {
      setAnimationData(cache.get(src)!);
      return;
    }
    fetch(src)
      .then((r) => {
        if (!r.ok) throw new Error(`Failed to load animation: ${src} (${r.status})`);
        return r.json();
      })
      .then((data) => {
        cache.set(src, data);
        setAnimationData(data);
      })
      .catch((err) => {
        if (process.env.NODE_ENV === 'development') console.warn('[LazyLottie]', err);
      });
  }, [isVisible, src]);

  return (
    <div ref={containerRef} className={className} style={style}>
      {animationData && (
        <Lottie
          animationData={animationData}
          loop={loop}
          autoplay={autoplay}
          className="w-full h-full"
        />
      )}
    </div>
  );
}
